import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import ProgressDashes from '../../components/ProgressDashes';
import { StoryNavigation } from '../../components/StoryNavigation';
import { useStoryNavigation } from '../../hooks/useStoryNavigation';

const SpendingPage = () => {
  const transactions = useSelector((state: RootState) => state.transactions.items);
  const [income, setIncome] = useState(0);
  const [spending, setSpending] = useState(0);
  const [balance, setBalance] = useState(0);
  const [savingsRate, setSavingsRate] = useState(0);
  const navigate = useNavigate();
  const { goToNext, goToPrev, hasNext, hasPrev } = useStoryNavigation();

  useEffect(() => {
    if (!transactions || transactions.length === 0) {
      navigate('/');
      return;
    }

    try {
      // Sort transactions by date to ensure correct balance calculation
      const sortedTransactions = [...transactions].sort((a, b) => {
        // Ensure we have valid dates
        if (!a.date || !b.date) return 0;
        return new Date(b.date).getTime() - new Date(a.date).getTime(); // Sort in descending order
      });
      
      console.log('All transactions before any filtering:', sortedTransactions);

      // Get the most recent transaction that has a balance
      const mostRecentTransaction = sortedTransactions.find(t => 
        typeof t.total_balance === 'number' && !isNaN(t.total_balance)
      );
      
      console.log('Most recent transaction with balance:', mostRecentTransaction);
      
      const currentBalance = mostRecentTransaction?.total_balance ?? 0;
      console.log('Current balance from most recent transaction:', currentBalance, 
                  'Date:', mostRecentTransaction?.date);

      // Filter out transfers ONLY for income/spending calculations, not for balance
      const realTransactions = sortedTransactions.filter(t => {
        // Skip transactions with no amount
        if (!t.amount && t.amount !== 0) return false;

        // Check if any category in the array includes 'transfer'
        if (Array.isArray(t.category) && t.category.some(cat => 
          cat.toLowerCase().includes('transfer') || 
          cat.toLowerCase().includes('credit') || 
          cat.toLowerCase().includes('payment'))) {
          return false;
        }
        
        // Fallback to description check if category is not available
        const description = t.description?.toLowerCase() || '';
        const isTransfer = description.includes('transfer') || 
                          description.includes('moved money') ||
                          description.includes('savings');
        return !isTransfer;
      });

      console.log('After filtering transfers:', realTransactions);

      // Calculate real income (positive transactions)
      const incomeTransactions = realTransactions.filter(t => t.amount > 0);
      const totalIncome = incomeTransactions.reduce((sum, t) => sum + t.amount, 0);
      console.log('Income transactions:', incomeTransactions);
      console.log('Total income:', totalIncome);

      // Calculate real spending (negative transactions)
      const spendingTransactions = realTransactions.filter(t => t.amount < 0);
      const totalSpending = spendingTransactions.reduce((sum, t) => sum + Math.abs(t.amount), 0);
      console.log('Spending transactions:', spendingTransactions);
      console.log('Total spending:', totalSpending);

      setIncome(totalIncome);
      setSpending(totalSpending);
      setBalance(currentBalance);

      // Calculate savings rate
      const savingsRate = totalIncome > 0 ? ((totalIncome - totalSpending) / totalIncome) * 100 : 0;
      console.log('Calculated savings rate:', savingsRate);
      setSavingsRate(savingsRate);
    } catch (error) {
      console.error('Error calculating totals:', error);
    }
  }, [transactions, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/insights/top-purchases');
    }, 10000);

    return () => clearTimeout(timer);
  }, [navigate]);

  if (!transactions || transactions.length === 0) {
    return null;
  }

  const formatCurrency = (amount: number, showCents = false) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: showCents ? 2 : 0
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-[#8364FF]/[0.07] relative overflow-hidden">
      <StoryNavigation
        onNext={goToNext}
        onPrev={goToPrev}
        hasNext={hasNext}
        hasPrev={hasPrev}
      />
      
      <div className="px-4 sm:px-6 md:px-16 pt-16 sm:pt-20 max-w-4xl mx-auto">
        <ProgressDashes currentStep={0} totalSteps={11} />

        <div className="mt-6 sm:mt-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-2"
          >
            <h1 className="text-[26px] leading-[140%] font-black text-[#221A51]
                         min-[320px]:text-[28px]
                         min-[375px]:text-[30px]
                         min-[428px]:text-[34px]
                         min-[912px]:text-[40px]
                         lg:text-[42px]">
              Your Spending
              <br />
              Overview
            </h1>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8 sm:mt-12"
          >
            <div className="bg-white rounded-xl p-4 sm:p-6 md:p-8 border-2 border-[#221A51] 
                         shadow-[4px_4px_0px_0px_#221A51] relative">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="space-y-6 sm:space-y-8"
              >
                {/* Income Section */}
                <motion.div
                  initial={{ x: -20 }}
                  animate={{ x: 0 }}
                  className="relative"
                >
                  <div className="text-xs sm:text-sm text-[#221A51]/60 uppercase tracking-wider mb-1 sm:mb-2">
                    Income
                  </div>
                  <div className="flex items-baseline gap-2 sm:gap-3">
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="text-[32px] min-[375px]:text-[36px] sm:text-[42px] md:text-[48px] 
                               font-black text-[#8364FF]"
                    >
                      {formatCurrency(income)}
                    </motion.div>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.5 }}
                      className="text-xs sm:text-sm text-[#8364FF]/40"
                    >
                      earned
                    </motion.div>
                  </div>
                </motion.div>
                
                {/* Spending Section */}
                <motion.div
                  initial={{ x: -20 }}
                  animate={{ x: 0 }}
                  className="relative"
                >
                  <div className="text-xs sm:text-sm text-[#221A51]/60 uppercase tracking-wider mb-1 sm:mb-2">
                    Spending
                  </div>
                  <div className="flex items-baseline gap-2 sm:gap-3">
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.6 }}
                      className="text-[32px] min-[375px]:text-[36px] sm:text-[42px] md:text-[48px] 
                               font-black text-[#FF6B6B]"
                    >
                      {formatCurrency(spending)}
                    </motion.div>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.7 }}
                      className="text-xs sm:text-sm text-[#FF6B6B]/40"
                    >
                      spent
                    </motion.div>
                  </div>
                </motion.div>

                {/* Balance Section */}
                <motion.div
                  initial={{ x: -20 }}
                  animate={{ x: 0 }}
                  className="relative"
                >
                  <div className="text-xs sm:text-sm text-[#221A51]/60 uppercase tracking-wider mb-1 sm:mb-2">
                    Balance
                  </div>
                  <div className="flex items-baseline gap-2 sm:gap-3">
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.8 }}
                      className="text-[32px] min-[375px]:text-[36px] sm:text-[42px] md:text-[48px] 
                               font-black text-[#221A51]"
                    >
                      {formatCurrency(balance)}
                    </motion.div>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.9 }}
                      className="text-xs sm:text-sm text-[#221A51]/40"
                    >
                      total
                    </motion.div>
                  </div>
                </motion.div>

                {/* Savings Rate Section */}
                <motion.div
                  initial={{ x: -20 }}
                  animate={{ x: 0 }}
                  className="relative"
                >
                  <div className="text-xs sm:text-sm text-[#221A51]/60 uppercase tracking-wider mb-1 sm:mb-2">
                    Savings Rate
                  </div>
                  <div className="flex items-baseline gap-2 sm:gap-3">
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1.0 }}
                      className="text-[32px] min-[375px]:text-[36px] sm:text-[42px] md:text-[48px] 
                               font-black text-[#4CAF50]"
                    >
                      {Math.round(savingsRate)}%
                    </motion.div>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 1.1 }}
                      className="text-xs sm:text-sm text-[#4CAF50]/40"
                    >
                      saved
                    </motion.div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SpendingPage;